import { create } from 'zustand'

import { captureException } from '@/services/monitoring'
import {
  getTicket,
  getTickets,
  postTicket,
  postTicketMessage,
} from '@/services/support'
import {
  withMessagePreview,
  withOpeningPreview,
  withPreservedPreview,
} from './messagePreviews'
import {
  createPendingMessage,
  type PendingMessage,
  withFailedPendingMessage,
  withoutEchoedPendingMessage,
  withoutPendingMessage,
  withRetriedPendingMessage,
} from './optimisticMessages'
import { clearReadReports } from './readReceipts'
import type {
  CreateTicketPayload,
  SupportMessage,
  SupportTicket,
} from './types'
import { staffTypingActions } from './typingStore'

const LAST_SEEN_STORAGE_KEY = 'counselpro:support:last-seen-at'

type LastSeenByTicket = Record<string, string>

const readLastSeen = (): LastSeenByTicket => {
  try {
    const raw = localStorage.getItem(LAST_SEEN_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

const writeLastSeen = (lastSeenAt: LastSeenByTicket) => {
  try {
    localStorage.setItem(LAST_SEEN_STORAGE_KEY, JSON.stringify(lastSeenAt))
  } catch {
    // ignore write failures (private mode / disabled storage)
  }
}

const byNewestMessage = (first: SupportTicket, second: SupportTicket) =>
  second.lastMessageAt.localeCompare(first.lastMessageAt)

// Every ticket entering the store goes through here, so a payload that carries
// no preview fields can never blank a row the list already filled in.
const withPreviewFromList = (
  tickets: SupportTicket[],
  incoming: SupportTicket,
) =>
  withPreservedPreview(
    incoming,
    tickets.find((existing) => existing.publicId === incoming.publicId),
  )

const withTicket = (tickets: SupportTicket[], ticket: SupportTicket) =>
  [
    ...tickets.filter((existing) => existing.publicId !== ticket.publicId),
    ticket,
  ].sort(byNewestMessage)

// Same order the server sends, so a merged thread reads like a fetched one.
const byOldestFirst = (first: SupportMessage, second: SupportMessage) =>
  first.createdAt.localeCompare(second.createdAt) || first.id - second.id

// Only ever merge within one thread: ids are unique per server, not per ticket.
const mergeMessages = (
  existing: SupportMessage[],
  incoming: SupportMessage[],
) => {
  const byId = new Map(existing.map((message) => [message.id, message]))
  for (const message of incoming) byId.set(message.id, message)
  return [...byId.values()].sort(byOldestFirst)
}

export const isTicketUnread = (
  ticket: SupportTicket,
  lastSeenAt: LastSeenByTicket,
) => {
  const seenAt = lastSeenAt[ticket.publicId]
  return !seenAt || ticket.lastMessageAt > seenAt
}

const buildTicketForm = (payload: CreateTicketPayload): FormData => {
  const form = new FormData()
  form.set('subject', payload.subject)
  form.set('description', payload.description)
  if (payload.projectName) form.set('projectName', payload.projectName)
  form.set('consent', String(payload.consent))
  // The service derives identity from the JWT and ignores these two fields,
  // but the dialog has always sent them; keep the payload intact.
  form.set('email', payload.email)
  form.set('userName', payload.userName)
  for (const file of payload.files ?? []) form.append('files', file)
  return form
}

// Only ever compared with other pending replies from this page load.
let pendingMessageCount = 0
const nextPendingMessageId = () => {
  pendingMessageCount += 1
  return `pending-${pendingMessageCount}`
}

const buildMessageForm = (body: string, files: File[]): FormData => {
  const form = new FormData()
  form.set('body', body)
  for (const file of files) form.append('files', file)
  return form
}

interface SupportStoreActions {
  upsertTicket: (ticket: SupportTicket) => void
  applyTicketUpdate: (ticket: SupportTicket) => void
  appendMessage: (ticketPublicId: string, message: SupportMessage) => void
  markTicketSeen: (ticketPublicId: string, seenAt?: string) => void
  clearActiveTicket: () => void
  reset: () => void
}

interface SupportStoreLoaders {
  fetchTickets: () => Promise<void>
  fetchTicket: (publicId: string) => Promise<void>
}

interface SupportStoreMutations {
  createTicket: (payload: CreateTicketPayload) => Promise<SupportTicket>
  // Resolves to whether the service confirmed the reply, so the caller can let
  // go of the stored draft only once it is safe.
  sendMessage: (
    ticketPublicId: string,
    body: string,
    files?: File[],
  ) => Promise<boolean>
  retryPendingMessage: (pendingId: string) => Promise<boolean>
  discardPendingMessage: (pendingId: string) => void
}

interface SupportStore {
  tickets: SupportTicket[]
  loadingTickets: boolean
  activeTicket: SupportTicket | null
  activeMessages: SupportMessage[]
  pendingMessages: PendingMessage[]
  loadingTicket: boolean
  mutating: boolean
  lastSeenAt: LastSeenByTicket
  actions: SupportStoreActions
  loaders: SupportStoreLoaders
  mutations: SupportStoreMutations
}

export const useSupportStore = create<SupportStore>((set, get) => {
  // Loads can overlap, so each carries an id. A result is dropped only when a
  // newer one already landed, never merely because a newer one started: a slow
  // success still beats a fast failure. Sign-out and closing a thread bump the
  // ids so a reply in flight cannot repopulate what the user just left.
  let ticketsLoadId = 0
  let appliedTicketsLoadId = 0
  let ticketsInFlight = 0
  let ticketLoadId = 0
  let ticketInFlight = 0

  // A fresh device has no watermarks; without seeding, every old ticket
  // (resolved ones included) would count as an unread reply.
  const seedLastSeenIfEmpty = (tickets: SupportTicket[]) => {
    if (Object.keys(get().lastSeenAt).length > 0 || tickets.length === 0) return
    const lastSeenAt = Object.fromEntries(
      tickets.map((ticket) => [ticket.publicId, ticket.lastMessageAt]),
    )
    writeLastSeen(lastSeenAt)
    set({ lastSeenAt })
  }

  const mergeTicket = (incoming: SupportTicket) =>
    set((state) => {
      const ticket = withPreviewFromList(state.tickets, incoming)
      return {
        tickets: withTicket(state.tickets, ticket),
        activeTicket:
          state.activeTicket?.publicId === ticket.publicId
            ? ticket
            : state.activeTicket,
      }
    })

  const deliver = async (pending: PendingMessage): Promise<boolean> => {
    try {
      const { ticket, message } = await postTicketMessage(
        pending.ticketPublicId,
        buildMessageForm(pending.body, pending.files),
      )
      mergeTicket(ticket)
      // The stream echo may have dropped this pending already; removing it by
      // id after the merge covers the run where the response came first.
      get().actions.appendMessage(pending.ticketPublicId, message)
      set((state) => ({
        pendingMessages: withoutPendingMessage(
          state.pendingMessages,
          pending.id,
        ),
      }))
      get().actions.markTicketSeen(pending.ticketPublicId, ticket.lastMessageAt)
      return true
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
      )
      set((state) => ({
        pendingMessages: withFailedPendingMessage(
          state.pendingMessages,
          pending.id,
        ),
      }))
      return false
    }
  }

  return {
    tickets: [],
    loadingTickets: false,
    activeTicket: null,
    activeMessages: [],
    pendingMessages: [],
    loadingTicket: false,
    mutating: false,
    lastSeenAt: readLastSeen(),

    actions: {
      upsertTicket: mergeTicket,
      applyTicketUpdate: mergeTicket,

      appendMessage: (ticketPublicId, message) =>
        set((state) => {
          const isActiveThread = state.activeTicket?.publicId === ticketPublicId
          const isDuplicate =
            isActiveThread &&
            state.activeMessages.some((existing) => existing.id === message.id)
          const activeMessages =
            isActiveThread && !isDuplicate
              ? [...state.activeMessages, message]
              : state.activeMessages

          const ticket = state.tickets.find(
            (existing) => existing.publicId === ticketPublicId,
          )
          // The stream carries no preview, so the newest message is where the
          // list row's snippet comes from. A second delivery of a message the
          // thread already holds is not news, and on a tied timestamp it would
          // rewrite the row with the older text.
          const refreshedTicket =
            ticket && !isDuplicate && message.createdAt >= ticket.lastMessageAt
              ? withMessagePreview(
                  { ...ticket, lastMessageAt: message.createdAt },
                  message,
                )
              : null

          return {
            activeMessages,
            pendingMessages: withoutEchoedPendingMessage(
              state.pendingMessages,
              ticketPublicId,
              message,
            ),
            tickets: refreshedTicket
              ? withTicket(state.tickets, refreshedTicket)
              : state.tickets,
            activeTicket:
              refreshedTicket && isActiveThread
                ? refreshedTicket
                : state.activeTicket,
          }
        }),

      markTicketSeen: (ticketPublicId, seenAt) => {
        const stamp = seenAt ?? new Date().toISOString()
        const current = get().lastSeenAt[ticketPublicId]
        if (current && current >= stamp) return
        const lastSeenAt = { ...get().lastSeenAt, [ticketPublicId]: stamp }
        writeLastSeen(lastSeenAt)
        set({ lastSeenAt })
      },

      clearActiveTicket: () => {
        // Closing the thread cancels its load, or it would reopen on arrival.
        ticketLoadId += 1
        set({ activeTicket: null, activeMessages: [] })
      },

      // Called on sign-out so the next user on this device cannot see the
      // previous user's tickets from memory.
      reset: () => {
        ticketsLoadId += 1
        ticketLoadId += 1
        clearReadReports()
        staffTypingActions().reset()
        // Watermarks are per user. Kept, they would mark every ticket of the
        // next user on this device unread, since none of theirs are in the map.
        writeLastSeen({})
        set({
          tickets: [],
          activeTicket: null,
          activeMessages: [],
          pendingMessages: [],
          loadingTickets: false,
          loadingTicket: false,
          mutating: false,
          lastSeenAt: {},
        })
      },
    },

    loaders: {
      fetchTickets: async () => {
        const loadId = ++ticketsLoadId
        ticketsInFlight += 1
        set({ loadingTickets: true })
        try {
          const { tickets } = await getTickets()
          if (loadId <= appliedTicketsLoadId) return
          appliedTicketsLoadId = loadId
          set({ tickets: [...tickets].sort(byNewestMessage) })
          seedLastSeenIfEmpty(tickets)
        } finally {
          ticketsInFlight -= 1
          if (ticketsInFlight === 0) set({ loadingTickets: false })
        }
      },

      fetchTicket: async (publicId) => {
        const loadId = ++ticketLoadId
        const isReloadOfOpenThread = get().activeTicket?.publicId === publicId
        // Reloading the open thread keeps what is on screen. Any other thread
        // starts empty, so one thread's messages can never merge into another.
        if (!isReloadOfOpenThread) {
          const known = get().tickets.find(
            (ticket) => ticket.publicId === publicId,
          )
          set({ activeTicket: known ?? null, activeMessages: [] })
        }
        ticketInFlight += 1
        set({ loadingTicket: true })
        try {
          const { ticket, messages } = await getTicket(publicId)
          // Strictly the newest here: an older thread's messages must never
          // open over the one the user asked for, or over a closed panel.
          if (loadId !== ticketLoadId) return
          mergeTicket(ticket)
          set((state) => ({
            // This is what opens a thread, so the ticket becomes active even
            // when the list had never heard of it. The same preserve rule runs
            // here, or the snippet mergeTicket just kept would be lost again.
            activeTicket: withPreviewFromList(state.tickets, ticket),
            // A live event can land while this request is out. Merging keeps it
            // instead of overwriting with the older server snapshot.
            activeMessages: mergeMessages(state.activeMessages, messages),
          }))
        } finally {
          ticketInFlight -= 1
          if (ticketInFlight === 0) set({ loadingTicket: false })
        }
      },
    },

    mutations: {
      createTicket: async (payload) => {
        set({ mutating: true })
        try {
          const { ticket } = await postTicket(buildTicketForm(payload))
          const created = withOpeningPreview(ticket, payload.description)
          mergeTicket(created)
          get().actions.markTicketSeen(created.publicId, created.lastMessageAt)
          return created
        } finally {
          set({ mutating: false })
        }
      },

      // The reply is on screen before the request leaves, so the thread never
      // looks like it swallowed what the user wrote. A failed send keeps its
      // bubble with Retry and Discard on it.
      sendMessage: (ticketPublicId, body, files = []) => {
        const pending = createPendingMessage(
          ticketPublicId,
          body,
          files,
          new Date().toISOString(),
          nextPendingMessageId(),
        )
        set((state) => ({
          pendingMessages: [...state.pendingMessages, pending],
        }))
        return deliver(pending)
      },

      retryPendingMessage: async (pendingId) => {
        const pending = get().pendingMessages.find(
          (candidate) => candidate.id === pendingId,
        )
        if (!pending || !pending.failed) return false
        set((state) => ({
          pendingMessages: withRetriedPendingMessage(
            state.pendingMessages,
            pendingId,
          ),
        }))
        return deliver(pending)
      },

      discardPendingMessage: (pendingId) =>
        set((state) => ({
          pendingMessages: withoutPendingMessage(
            state.pendingMessages,
            pendingId,
          ),
        })),
    },
  }
})

export const useSupportTickets = () => useSupportStore((state) => state.tickets)

export const useSupportLoadingTickets = () =>
  useSupportStore((state) => state.loadingTickets)

export const useSupportActiveTicket = () =>
  useSupportStore((state) => state.activeTicket)

export const useSupportActiveMessages = () =>
  useSupportStore((state) => state.activeMessages)

export const useSupportPendingMessages = () =>
  useSupportStore((state) => state.pendingMessages)

export const useSupportLoadingTicket = () =>
  useSupportStore((state) => state.loadingTicket)

export const useSupportIsMutating = () =>
  useSupportStore((state) => state.mutating)

export const useSupportLastSeenAt = () =>
  useSupportStore((state) => state.lastSeenAt)

export const useSupportActions = () => useSupportStore((state) => state.actions)

export const useSupportLoaders = () => useSupportStore((state) => state.loaders)

export const useSupportMutations = () =>
  useSupportStore((state) => state.mutations)

export const useSupportUnreadCount = () =>
  useSupportStore(
    (state) =>
      state.tickets.filter((ticket) => isTicketUnread(ticket, state.lastSeenAt))
        .length,
  )

// Read at call time, so a click lands on whatever is newest right then.
// Tickets are always sorted newest message first.
export const newestUnreadTicket = (): SupportTicket | null => {
  const { tickets, lastSeenAt } = useSupportStore.getState()
  return tickets.find((ticket) => isTicketUnread(ticket, lastSeenAt)) ?? null
}
